/**
 * useComparisonMode Hook
 *
 * Manages record comparison mode state and preset strategy execution.
 *
 * Features:
 * - Enter/exit comparison mode
 * - Execute preset strategies (active_now, active_on, active_latest, active_prior, pick)
 * - Track anchor and comparison records
 * - Manage selector mode (same_entity vs any_record)
 */

import { useState, useCallback, useMemo } from 'react'
import { dataService } from '@/services/dataService'
import type { Filter } from '@/types/api.types'
import type {
  ComparisonConfig,
  ComparisonSelectorMode,
  UseComparisonModeResult,
  PresetExecutionResult,
  ComparisonStrategy,
} from './types'

/**
 * Default comparison config when none provided by metadata
 */
const DEFAULT_CONFIG: ComparisonConfig = {
  enabled: false,
  defaultMode: 'same_entity',
  allowSearchMode: true,
  maxVersionsEager: 50,
  paginationSize: 20,
  stateOrder: undefined,
  keyFields: [],
  excludeFields: [],
  panelTitle: 'Compare {entity} Versions',
  presets: undefined,
}

/**
 * Build filters for "effective at date" queries.
 * A record is effective at a date if:
 * effectiveFrom <= date AND (effectiveTo IS NULL OR effectiveTo >= date)
 */
function buildEffectiveAtDateFilters(date: string): Filter[] {
  return [
    { key: 'effectiveFrom', constraint_type: 'LE', value: date },
    {
      constraint_type: 'OR',
      filters: [
        { key: 'effectiveTo', constraint_type: 'IS_NULL' },
        { key: 'effectiveTo', constraint_type: 'GE', value: date },
      ],
    },
  ]
}

/**
 * Execute a preset strategy to find a comparison record.
 *
 * @param strategy - The strategy to execute
 * @param anchorRecord - The anchor record (left panel)
 * @param endpoint - API endpoint (e.g., '/offerings')
 * @param lookupDate - Optional date for 'active_on' strategy
 * @returns The found record and/or a message
 */
async function executeStrategy(
  strategy: ComparisonStrategy,
  anchorRecord: Record<string, unknown>,
  endpoint: string,
  lookupDate?: string | null
): Promise<PresetExecutionResult> {
  const keyComboHash = anchorRecord.keyComboHash as string | undefined
  const anchorUid = anchorRecord.uid as string
  const anchorEffectiveFrom = anchorRecord.effectiveFrom as string | null
  const now = new Date().toISOString()

  // Remove leading slash if present for dataService.search
  const tableName = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint

  // Base filters for all strategies
  const baseFilters: Filter[] = [
    { key: 'deletedAt', constraint_type: 'IS_NULL' },
    { key: 'uid', constraint_type: 'NOT_EQUAL', value: anchorUid },
  ]

  // Add keyComboHash filter if strategy requires same entity
  if (keyComboHash && strategy !== 'pick') {
    baseFilters.push({
      key: 'keyComboHash',
      constraint_type: 'EQUAL',
      value: keyComboHash,
    })
  }

  switch (strategy) {
    case 'active_now': {
      // "vs Now" - Find ACTIVE record currently in effect (live price)
      const response = await dataService.search(tableName, {
        filters: [
          ...baseFilters,
          { key: 'lifecycleState', constraint_type: 'EQUAL', value: 'active' },
          ...buildEffectiveAtDateFilters(now),
        ],
        order_by: [{ key: 'effectiveFrom', direction: 'desc' }],
        limit: 1,
      })

      if (!response.success || !response.data?.length) {
        return {
          record: null,
          message: 'No active version currently in effect',
        }
      }
      return { record: response.data[0] as Record<string, unknown> }
    }

    case 'active_on': {
      // "vs On" - Find ACTIVE record effective at a specific date
      const date = lookupDate || anchorEffectiveFrom
      if (!date) {
        return { record: null, message: 'No date specified' }
      }

      const response = await dataService.search(tableName, {
        filters: [
          ...baseFilters,
          { key: 'lifecycleState', constraint_type: 'EQUAL', value: 'active' },
          ...buildEffectiveAtDateFilters(date),
        ],
        order_by: [{ key: 'effectiveFrom', direction: 'desc' }],
        limit: 1,
      })

      if (!response.success || !response.data?.length) {
        const dateStr = new Date(date).toLocaleDateString()
        return { record: null, message: `No active version on ${dateStr}` }
      }
      return { record: response.data[0] as Record<string, unknown> }
    }

    case 'active_latest': {
      // "vs Latest" - Find active/expired record with max effectiveFrom
      const response = await dataService.search(tableName, {
        filters: [
          ...baseFilters,
          {
            key: 'lifecycleState',
            constraint_type: 'IN',
            values: ['active', 'expired'],
          },
          { key: 'effectiveFrom', constraint_type: 'IS_NOT_NULL' },
        ],
        order_by: [{ key: 'effectiveFrom', direction: 'desc' }],
        limit: 1,
      })

      if (!response.success || !response.data?.length) {
        return { record: null, message: 'No other versions found' }
      }
      return { record: response.data[0] as Record<string, unknown> }
    }

    case 'active_prior': {
      // "vs Prior" - Find active/expired record with max effectiveTo < NOW
      const response = await dataService.search(tableName, {
        filters: [
          ...baseFilters,
          {
            key: 'lifecycleState',
            constraint_type: 'IN',
            values: ['active', 'expired'],
          },
          { key: 'effectiveTo', constraint_type: 'IS_NOT_NULL' },
          { key: 'effectiveTo', constraint_type: 'LT', value: now },
        ],
        order_by: [{ key: 'effectiveTo', direction: 'desc' }],
        limit: 1,
      })

      if (!response.success || !response.data?.length) {
        return {
          record: null,
          message: 'No prior version - nothing has ended yet',
        }
      }
      return { record: response.data[0] as Record<string, unknown> }
    }

    case 'pick':
    default:
      // 'pick' strategy opens the selector - no auto-fetch
      return { record: null }
  }
}

interface UseComparisonModeOptions {
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Comparison configuration from metadata */
  config?: ComparisonConfig | null
}

/**
 * Hook for managing record comparison mode.
 *
 * @param options - Configuration options
 * @returns Comparison mode state and actions
 *
 * @example
 * ```tsx
 * const comparison = useComparisonMode({
 *   endpoint: '/offerings',
 *   config: metadata.comparison,
 * })
 *
 * // Enter comparison mode
 * comparison.enterComparisonMode(currentRecord)
 *
 * // Execute a preset
 * await comparison.executePreset('active_now')
 *
 * // Check if in comparison mode
 * if (comparison.isActive) {
 *   // Render comparison view
 * }
 * ```
 */
export function useComparisonMode({
  endpoint,
  config,
}: UseComparisonModeOptions): UseComparisonModeResult {
  // Merge with defaults
  const effectiveConfig = useMemo(
    () => ({ ...DEFAULT_CONFIG, ...config }),
    [config]
  )

  // Core state
  const [isActive, setIsActive] = useState(false)
  const [anchorRecord, setAnchorRecord] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [comparisonRecord, setComparisonRecord] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [selectorMode, setSelectorMode] = useState<ComparisonSelectorMode>(
    effectiveConfig.defaultMode === 'search' ? 'any_record' : 'same_entity'
  )

  // Preset state
  const [activePreset, setActivePreset] = useState<string | null>(null)
  const [isLoadingPreset, setIsLoadingPreset] = useState(false)
  const [lookupDate, setLookupDate] = useState<string | null>(null)
  const [presetError, setPresetError] = useState<string | null>(null)

  // Enter comparison mode with an anchor record
  const enterComparisonMode = useCallback(
    (record: Record<string, unknown>) => {
      setAnchorRecord(record)
      setComparisonRecord(null)
      setActivePreset(null)
      setPresetError(null)
      setIsActive(true)

      // If config has a default preset, execute it
      if (effectiveConfig.presets?.default) {
        // Use setTimeout to defer preset execution after state update
        setTimeout(() => {
          const defaultPreset = effectiveConfig.presets?.options.find(
            (p) => p.key === effectiveConfig.presets?.default
          )
          if (defaultPreset && defaultPreset.strategy !== 'pick') {
            // Trigger preset execution - handled by component
          }
        }, 0)
      }
    },
    [effectiveConfig.presets]
  )

  // Exit comparison mode
  const exitComparisonMode = useCallback(() => {
    setIsActive(false)
    setAnchorRecord(null)
    setComparisonRecord(null)
    setActivePreset(null)
    setPresetError(null)
    setIsLoadingPreset(false)
    setLookupDate(null)
  }, [])

  // Execute a preset strategy
  const executePreset = useCallback(
    async (presetKey: string, date?: string) => {
      if (!anchorRecord) {
        setPresetError('No anchor record available')
        return
      }

      // Find the preset
      const preset = effectiveConfig.presets?.options.find(
        (p) => p.key === presetKey
      )
      if (!preset) {
        setPresetError(`Unknown preset: ${presetKey}`)
        return
      }

      // If 'pick' strategy, just clear comparison and let selector open
      if (preset.strategy === 'pick') {
        setComparisonRecord(null)
        setActivePreset('pick')
        setPresetError(null)
        return
      }

      setIsLoadingPreset(true)
      setPresetError(null)
      setActivePreset(presetKey)

      try {
        const result = await executeStrategy(
          preset.strategy as ComparisonStrategy,
          anchorRecord,
          endpoint,
          date || lookupDate
        )

        if (result.record) {
          setComparisonRecord(result.record)
          setPresetError(null)
        } else {
          setComparisonRecord(null)
          setPresetError(result.message || 'No matching record found')
        }
      } catch (error) {
        console.error('Failed to execute preset:', error)
        setPresetError(
          error instanceof Error ? error.message : 'Failed to find record'
        )
        setComparisonRecord(null)
      } finally {
        setIsLoadingPreset(false)
      }
    },
    [anchorRecord, endpoint, effectiveConfig.presets, lookupDate]
  )

  // Clear preset error
  const clearPresetError = useCallback(() => {
    setPresetError(null)
  }, [])

  return {
    // State
    isActive,
    anchorRecord,
    comparisonRecord,
    selectorMode,
    activePreset,
    isLoadingPreset,
    lookupDate,
    presetError,

    // Config
    config: effectiveConfig,

    // Actions
    enterComparisonMode,
    exitComparisonMode,
    setComparisonRecord,
    setSelectorMode,
    executePreset,
    setLookupDate,
    clearPresetError,
  }
}

export default useComparisonMode
